import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { getAuthConfig } from "../../utils/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Switch } from "../ui/switch";
import { Landmark, RefreshCw, AlertTriangle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  calculated: { label: "Calculated", cls: "bg-teal-500/15 text-teal-600 dark:text-teal-400" },
  zero_tax_jurisdiction: { label: "Zero Tax Jurisdiction", cls: "bg-sky-500/15 text-sky-600 dark:text-sky-400" },
  tax_exempt: { label: "Tax Exempt", cls: "bg-purple-500/15 text-purple-600 dark:text-purple-400" },
  manual_override: { label: "Manual Override", cls: "bg-amber-500/15 text-amber-600 dark:text-amber-400" },
  needs_address: { label: "Needs Address", cls: "bg-orange-500/15 text-orange-600 dark:text-orange-400" },
  calculation_failed: { label: "Calculation Failed", cls: "bg-red-500/15 text-red-600 dark:text-red-400" },
  disabled: { label: "Disabled", cls: "bg-zinc-500/15 text-zinc-600 dark:text-zinc-400" },
};

const isAdminUser = () => {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    return !u.role || ["owner", "admin", "dispatcher"].includes(u.role);
  } catch { return false; }
};

/**
 * Automatic tax panel for the job form.
 * Live preview from the SERVICE address; override/remove for saved jobs.
 * Renders nothing when the business hasn't enabled automatic tax.
 */
export const JobTaxPanel = ({ jobId, address, city, state, zip, amount, customerId, locationId, savedJob, onTaxChange, onPreviewChange, taxDisabled = false, onTaxDisabledChange, onOverrideDraftChange }) => {
  const [enabled, setEnabled] = useState(null);
  const [overrideAllowed, setOverrideAllowed] = useState(true);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showOverride, setShowOverride] = useState(false);
  const [ovr, setOvr] = useState({ mode: "rate", value: "", reason: "" });
  const [jobTax, setJobTax] = useState(null);
  // Custom tax set on a NEW job — sent with the create payload via the parent.
  const [draftOverride, setDraftOverride] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/tax/settings`, { params: locationId ? { location_id: locationId } : {}, ...getAuthConfig() })
      .then((r) => { setEnabled(!!r.data.charge_tax); setOverrideAllowed(r.data.manual_override_allowed !== false); })
      .catch(() => setEnabled(false));
  }, [locationId]);

  useEffect(() => {
    if (savedJob?.tax_status) {
      setJobTax({
        tax_status: savedJob.tax_status, tax_rate: savedJob.tax_rate, tax_amount: savedJob.tax_amount,
        tax_jurisdiction: savedJob.tax_jurisdiction, tax_breakdown: savedJob.tax_breakdown,
        tax_note: savedJob.tax_note, tax_override: savedJob.tax_override,
      });
    }
  }, [savedJob]);

  const fetchPreview = useCallback(() => {
    if (!enabled || taxDisabled) return;
    setLoading(true);
    axios.post(`${API}/tax/preview`, {
      address: address || "", city: city || "", state: state || "", zip_code: zip || "",
      amount: parseFloat(amount) || 0,
      customer_id: customerId || null, location_id: locationId || null,
    }, getAuthConfig())
      .then((r) => setPreview(r.data))
      .catch(() => setPreview({ status: "calculation_failed", note: "Unable to verify tax rate" }))
      .finally(() => setLoading(false));
  }, [enabled, address, city, state, zip, amount, customerId, locationId]);

  useEffect(() => {
    if (!enabled || taxDisabled) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchPreview, 600);
    return () => clearTimeout(debounceRef.current);
  }, [fetchPreview, enabled, taxDisabled]);

  // Share the live preview with the parent (Job Completion financial summary).
  useEffect(() => {
    onPreviewChange?.(enabled && !taxDisabled ? preview : null);
  }, [preview, enabled, taxDisabled, onPreviewChange]);

  if (!enabled) return null;

  // A saved job's stored snapshot wins over the live preview when it carries
  // an override or exemption; otherwise show the live preview. A draft
  // override on a NEW job is computed client-side until save.
  const amtNum = parseFloat(amount) || 0;
  const draftView = draftOverride ? {
    tax_status: "manual_override",
    tax_rate: draftOverride.mode === "rate"
      ? (parseFloat(draftOverride.value) || 0)
      : (amtNum > 0 ? Math.round(((parseFloat(draftOverride.value) || 0) / amtNum) * 10000) / 100 : 0),
    tax_amount: draftOverride.mode === "amount"
      ? (parseFloat(draftOverride.value) || 0)
      : Math.round(amtNum * (parseFloat(draftOverride.value) || 0)) / 100,
    tax_note: `Manual override: ${draftOverride.reason}`,
  } : null;
  const overridden = !!draftView || jobTax?.tax_status === "manual_override";
  const view = draftView || (jobTax?.tax_status === "manual_override" ? jobTax : (preview || jobTax));
  const status = taxDisabled ? "disabled" : (view?.tax_status || view?.status);
  const meta = STATUS_META[status] || STATUS_META.calculated;
  const rate = view?.tax_rate ?? view?.rate;
  const taxAmount = view?.tax_amount;
  const jurisdiction = view?.tax_jurisdiction || view?.jurisdiction;
  const breakdown = view?.tax_breakdown || view?.components || [];
  const note = view?.tax_note || view?.note;

  const submitOverride = async () => {
    if (!ovr.reason.trim()) { toast.error("A reason is required to override tax"); return; }
    if (!jobId) {
      const draft = { mode: ovr.mode, value: parseFloat(ovr.value) || 0, reason: ovr.reason.trim() };
      setDraftOverride(draft);
      onOverrideDraftChange?.(draft);
      setShowOverride(false);
      toast.success("Custom tax will be applied when the job is saved");
      return;
    }
    try {
      const r = await axios.post(`${API}/tax/jobs/${jobId}/override`, {
        mode: ovr.mode, value: parseFloat(ovr.value) || 0, reason: ovr.reason.trim(),
      }, getAuthConfig());
      setJobTax(r.data);
      onTaxChange?.(r.data);
      setShowOverride(false);
      toast.success("Tax override applied");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to override tax");
    }
  };

  const removeOverride = async () => {
    if (!jobId) {
      setDraftOverride(null);
      onOverrideDraftChange?.(null);
      toast.success("Custom tax removed");
      return;
    }
    try {
      const r = await axios.delete(`${API}/tax/jobs/${jobId}/override`, getAuthConfig());
      setJobTax(r.data);
      onTaxChange?.(r.data);
      toast.success("Override removed — automatic tax restored");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove override");
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card/50 p-3.5 space-y-2.5" data-testid="job-tax-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Landmark className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-semibold text-foreground">Tax</span>
          <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Automatic</span>
          <div className="flex items-center gap-1.5 ml-1" title={taxDisabled ? "Tax is disabled for this job" : "Turn off to charge no tax on this job"}>
            <Switch
              checked={!taxDisabled}
              onCheckedChange={(v) => onTaxDisabledChange?.(!v)}
              className="scale-[0.7] -mx-1"
              data-testid="job-tax-toggle"
            />
            <span className="text-[10px] text-muted-foreground">{taxDisabled ? "Off" : "On"}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {status && (
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${meta.cls}`} data-testid="job-tax-status">
              {meta.label}
            </span>
          )}
          {!taxDisabled && status === "calculation_failed" && (
            <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={fetchPreview} data-testid="job-tax-retry-btn">
              <RefreshCw className="w-3 h-3 mr-1" /> Retry
            </Button>
          )}
        </div>
      </div>

      {taxDisabled ? (
        <p className="text-xs text-muted-foreground" data-testid="job-tax-disabled-note">
          Tax is disabled for this job — no tax will be added to the customer total.
        </p>
      ) : loading && !view ? (
        <p className="text-xs text-muted-foreground">Calculating tax…</p>
      ) : status === "needs_address" ? (
        <p className="text-xs text-orange-500 flex items-center gap-1.5" data-testid="job-tax-needs-address">
          <AlertTriangle className="w-3.5 h-3.5" /> Tax calculation requires a complete service address (street, city, state/province, ZIP/postal code)
        </p>
      ) : status === "calculation_failed" ? (
        <p className="text-xs text-red-500 flex items-center gap-1.5" data-testid="job-tax-failed">
          <AlertTriangle className="w-3.5 h-3.5" /> {note || "Unable to verify tax rate — this is NOT a 0% jurisdiction"}
        </p>
      ) : view ? (
        <div className="space-y-1.5">
          {jurisdiction && (
            <p className="text-xs text-muted-foreground" data-testid="job-tax-jurisdiction">
              Jurisdiction: <span className="text-foreground">{jurisdiction}</span>
            </p>
          )}
          {status !== "manual_override" && (breakdown || []).map((c, i) => (
            <div key={i} className="flex justify-between text-xs text-muted-foreground" data-testid={`job-tax-component-${i}`}>
              <span>{c.name || c.type} ({c.rate}%)</span>
              <span>{typeof c.amount === "number" ? `$${c.amount.toFixed(2)}` : ""}</span>
            </div>
          ))}
          <div className="flex justify-between text-sm font-medium pt-1 border-t border-border/60">
            <span className="text-foreground">
              {status === "tax_exempt" ? (
                <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-purple-500" /> Tax Exempt</span>
              ) : `Total Tax${typeof rate === "number" ? ` (${rate}%)` : ""}`}
            </span>
            <span className="text-teal-600 dark:text-teal-400" data-testid="job-tax-total">
              +${(typeof taxAmount === "number" ? taxAmount : 0).toFixed(2)}
            </span>
          </div>
          {note && <p className="text-[11px] text-muted-foreground">{note}</p>}
        </div>
      ) : null}

      {!taxDisabled && isAdminUser() && overrideAllowed && (
        <div className="flex items-center gap-2 pt-1">
          {overridden ? (
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={removeOverride} data-testid="job-tax-remove-override-btn">
              Remove Override
            </Button>
          ) : (
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowOverride(true)} data-testid="job-tax-override-btn">
              Override Tax
            </Button>
          )}
        </div>
      )}

      <Dialog open={showOverride} onOpenChange={setShowOverride}>
        <DialogContent className="max-w-md" data-testid="job-tax-override-dialog">
          <DialogHeader><DialogTitle>Override Automatic Tax</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-amber-500 flex items-start gap-1.5">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              You're overriding the automatically calculated tax for this job. The original calculation and your reason will be recorded.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Override by</Label>
                <Select value={ovr.mode} onValueChange={(v) => setOvr({ ...ovr, mode: v })}>
                  <SelectTrigger className="mt-1 h-9" data-testid="tax-override-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rate">Custom rate (%)</SelectItem>
                    <SelectItem value="amount">Custom amount ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">{ovr.mode === "rate" ? "Rate %" : "Amount $"}</Label>
                <Input type="number" step="0.01" value={ovr.value} className="mt-1 h-9"
                  onChange={(e) => setOvr({ ...ovr, value: e.target.value })} data-testid="tax-override-value-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Reason (required)</Label>
              <Textarea value={ovr.reason} onChange={(e) => setOvr({ ...ovr, reason: e.target.value })}
                className="mt-1 min-h-[70px]" placeholder="Why is the automatic tax being overridden?" data-testid="tax-override-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOverride(false)}>Cancel</Button>
            <Button onClick={submitOverride} data-testid="tax-override-submit-btn">Apply Override</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobTaxPanel;
